import {
  action, makeAutoObservable, observable, observe, runInAction, when,
} from 'mobx';
import * as Sentry from '@sentry/react';
import { I18n, i18next } from '@gamiphy/react-i18n';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import {
  DisplayString,
  getCurrencyFormatter, getDiscountTypeFormatter, getPointsFormatter, Languages,
} from '@coretava/i18n';
import { ThemeStore } from '@gamiphy/loyalty-station-desktop/store/theme';
import { StringsApi } from '@gamiphy/gamix-modules/strings';
import { Currencies } from '@gamiphy/bl/core';
import { getLocale } from '@gamiphy/utils/locale';
import { DiscountsStore } from '@gamiphy/loyalty-station-desktop/store/discounts';
import { CoreConfig } from './types';

class CoreStore {
  @observable initialized = false;

  @observable visible: boolean = true;

  @observable country?: string = undefined;

  @observable currency?: Currencies = undefined;

  constructor() {
    makeAutoObservable(this);
  }

  @action
  async init(appId: string, config: CoreConfig): Promise<void> {
    try {
      await AppStore.init(appId);
      if (!AppStore.app) {
        return;
      }

      await Promise.all([
        when(() => ThemeStore.initialized),
        UserStore.login(config.user),
        this.initCurrency(),
        this.initTranslation(),
      ]);
      void DiscountsStore.initialize();
      this.initCountry();
      this.visible = config.visible ?? this.visible;

      runInAction(() => {
        this.initialized = true;
      });
    } catch (e) {
      console.error(e);
      Sentry.captureException(e);
    }
  }

  async initTranslation(): Promise<void> {
    await Promise.all([
      when(() => AppStore.initialized),
      when(() => ThemeStore.initialized),
      when(() => !!this.currency),
    ]);

    await I18n.initialize({
      language: (ThemeStore.theme.language ?? AppStore.app.language) as Languages,
      formatters: {
        ...getCurrencyFormatter(getLocale(), this.currency),
        ...getPointsFormatter(i18next, getLocale(), ThemeStore.theme.points.gems.name as DisplayString, ThemeStore.theme.points.exp.name as DisplayString),
        ...getDiscountTypeFormatter(i18next, AppStore.app.agent),
      },
      detectionOrder: ThemeStore.theme.language ? ['providedLanguage'] : ['htmlTag', 'providedLanguage'],
      customStrings: await StringsApi.getStrings(),
    });
  }

  initCountry(): void {
    observe(UserStore, 'user', () => {
      if (!this.country && UserStore.user?.country) {
        this.country = UserStore.user?.country;
      }
    });
  }

  async initCurrency(): Promise<void> {
    await when(() => AppStore.initialized && UserStore.initialized);

    runInAction(() => {
      this.currency = (UserStore.user?.currency ?? AppStore.app.currency) as Currencies;
    });

    window.GAMIPHY_CURRENCY_CODE = this.currency;
  }
}

export default new CoreStore();
